import axios from 'axios';
import dayjs from 'dayjs';
import { SubscriptionApiType } from '../types/api/SubscriptionApiType';
import { Setting } from '../types/constant/PagesPropsType';
import { ChangeDeliveryDateModalViewType } from '../types/view/ChangeDeliveryDateModalViewType';
import { CUSTOMER_API_URL } from '../constants';
import { changeableTerm } from '../constants/parameters';

type UpdateNextDeliveryDateDataType = {
  subscriptionContractId: string;
  nextDeliveryDate: string;
  deliveryTime: string | null;
};

export const useUpdateNextDeliveryDate = () => {
  const updateNextDeliveryDate = (
    setting: Setting,
    data: UpdateNextDeliveryDateDataType,
    success: (
      nextBillingDate: Date,
      nextDeliveryDate: Date,
      deliveryDays: number,
      deliveryTime: string | null,
      deliveryTimeText: string | null
    ) => void,
    failed: () => void,
    completion: () => void
  ) => {
    axios
      .post(
        `${CUSTOMER_API_URL}?action=updateNextDeliveryDate`,
        { ...data },
        { headers: { Authorization: setting.token } }
      )
      .then((response) => {
        success(
          response.data.nextBillingDate,
          response.data.nextDeliveryDate,
          response.data.deliveryDays,
          response.data.deliveryTime,
          response.data.deliveryTimeText
        );
      })
      .catch((e) => {
        console.log(e);
        failed();
      })
      .finally(() => completion());
  };

  /**
   * 定期購買のお届け日を更新する
   *
   * @param setting
   * @param subscription
   * @param changeDeliveryDateModal
   * @param success
   * @param failed
   * @param completion
   */
  const updateDeliveryDays = (
    setting: Setting,
    subscription: SubscriptionApiType,
    changeDeliveryDateModal: ChangeDeliveryDateModalViewType,
    success: (
      nextBillingDate: Date,
      nextDeliveryDate: Date,
      deliveryDays: number,
      deliveryTime: string | null,
      deliveryTimeText: string | null
    ) => void,
    failed: () => void,
    completion: () => void
  ) => {
    const data: UpdateNextDeliveryDateDataType = {
      subscriptionContractId: subscription.subscriptionContractId,
      nextDeliveryDate: dayjs(changeDeliveryDateModal.nextDeliveryDate).format('YYYY-MM-DD'),
      deliveryTime:
        changeDeliveryDateModal.deliveryTime !== '' ? changeDeliveryDateModal.deliveryTime : null,
    };

    updateNextDeliveryDate(setting, data, success, failed, completion);
  };

  const skipDeliveryDays = (
    setting: Setting,
    subscription: SubscriptionApiType,
    skipSpan: number,
    success: (
      nextBillingDate: Date,
      nextDeliveryDate: Date,
      deliveryDays: number,
      deliveryTime: string | null,
      deliveryTimeText: string | null
    ) => void,
    failed: () => void,
    completion: () => void
  ) => {
    const data: UpdateNextDeliveryDateDataType = {
      subscriptionContractId: subscription.subscriptionContractId,
      nextDeliveryDate: dayjs(subscription.nextDeliveryDate)
        .add(skipSpan, 'w')
        .format('YYYY-MM-DD'),
      deliveryTime: subscription.deliveryTime,
    };

    updateNextDeliveryDate(setting, data, success, failed, completion);
  };

  /**
   * お届け日の変更が可能か判定する
   *
   * @param subscription
   */
  const validUpdateDeliveryDays = (subscription: SubscriptionApiType) => {
    const now = dayjs().set('hour', 0).set('minute', 0).set('second', 0).set('millisecond', 0);
    const nextBillingDate = dayjs(subscription.nextBillingDate)
      .set('minute', 0)
      .set('second', 0)
      .set('millisecond', 0);

    const errors: string[] = [];
    if (!(nextBillingDate.diff(now, 'day') >= changeableTerm)) {
      errors.push(
        `${nextBillingDate
          .subtract(changeableTerm, 'day')
          .format('YYYY/MM/DD')}を過ぎているため、お届け日を更新できません`
      );
    }
    return errors;
  };

  return {
    skipDeliveryDays,
    updateDeliveryDays,
    validUpdateDeliveryDays,
  };
};
